import { CompactVenueCard } from './CompactVenueCard';
import type { CompactVenueCardProps } from './CompactVenueCard';
import type { DistanceLocale } from '@wineryxplorer/shared';

interface EmptyStateProps {
  hasActiveFilters: boolean;
  hasDivergedViewport: boolean;
  onClearFilters: () => void;
  onZoomOut?: () => void;
  fallbackItems: CompactVenueCardProps['venue'][];
  userLocation?: { lat: number; lng: number } | null;
  distanceLocale?: DistanceLocale;
}

export const EmptyState = ({
  hasActiveFilters,
  hasDivergedViewport,
  onClearFilters,
  onZoomOut,
  fallbackItems,
  userLocation,
  distanceLocale,
}: EmptyStateProps) => {
  const hasFallback = fallbackItems.length > 0;

  return (
    <div className="py-8">
      <div className="text-center">
        <svg
          className="mx-auto h-12 w-12 text-gray-400"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
          />
        </svg>
        <h3 className="mt-2 text-sm font-medium text-gray-900">
          {hasFallback ? 'No exact matches in this view' : 'No wineries match your search'}
        </h3>
        <p className="mt-1 text-sm text-gray-500">
          {hasFallback
            ? 'Try clearing some filters or zooming out.'
            : 'Try a broader area or different filters.'}
        </p>
        <div className="mt-3 flex justify-center gap-2 flex-wrap">
          {hasActiveFilters && (
            <button
              type="button"
              onClick={onClearFilters}
              className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-primary-800 bg-primary-50 rounded-lg hover:bg-primary-100 transition-colors"
            >
              Clear all filters
            </button>
          )}
          {hasDivergedViewport && onZoomOut && (
            <button
              type="button"
              onClick={onZoomOut}
              className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-primary-800 bg-primary-50 rounded-lg hover:bg-primary-100 transition-colors"
            >
              Zoom map out
            </button>
          )}
        </div>
      </div>

      {hasFallback && (
        <div className="mt-8">
          <div className="flex items-center gap-3 mb-3">
            <div className="h-px flex-1 bg-gray-200" />
            <h4 className="text-sm font-semibold text-gray-700">Other wineries nearby</h4>
            <div className="h-px flex-1 bg-gray-200" />
          </div>
          <div className="space-y-3">
            {fallbackItems.map((venue) => (
              <CompactVenueCard
                key={venue._id}
                venue={venue}
                userLocation={userLocation}
                distanceLocale={distanceLocale}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
